import React, { useCallback, useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import SpecialRequestsPanel from "../flights/SpecialRequestsPanel";
import { useAuth } from "../../context/AuthContext";
import { isStaff } from "../../utils/roles";
import { auth } from "../../firebase";
import {
  cancelBooking, searchExchangeOffers,
  requestCancelOtp, resendCancelOtp, CancelOtpChallenge,
} from "../../services/api";
import { exchangeEnabled, refundEnabled, ticketChangesEnabled } from "../../utils/features";
import {
  FaPlane, FaClock, FaUser, FaLuggageCart,
  FaPlaneDeparture, FaPlaneArrival,
  FaDownload, FaChevronDown, FaChevronUp, FaCalendarAlt,
  FaCheckCircle, FaTimesCircle, FaExchangeAlt, FaSuitcase, FaClipboardList,
} from "react-icons/fa";

// ── Airline / cabin maps ──────────────────────────────────────────────────────
const AIRLINE_NAMES: Record<string, string> = {
  WY: "Oman Air", EY: "Etihad Airways", EK: "Emirates", QR: "Qatar Airways",
  AI: "Air India", "6E": "IndiGo", SG: "SpiceJet", QP: "Akasa Air",
  FZ: "Flydubai", TK: "Turkish Airlines", SV: "Saudia", G9: "Air Arabia",
  GF: "Gulf Air", KU: "Kuwait Airways", RJ: "Royal Jordanian",
  ME: "Middle East Airlines", MS: "EgyptAir", XY: "Flynas",
  PK: "PIA", IX: "Air India Express",
};

const CABIN_NAMES: Record<string, string> = {
  Y: "Economy", S: "Premium Economy", C: "Business",
  J: "Premium Business", F: "First Class", P: "Premium First",
};

// ── Pure helper functions ─────────────────────────────────────────────────────

/** Extract HH:MM from "HH:MM:SS±HH:MM" or ISO datetime */
function extractHHMM(t?: string): string {
  if (!t) return "--:--";
  const m = t.match(/(\d{2}:\d{2})/);
  return m ? m[1] : "--:--";
}

/** Format elapsed minutes as "Xh Ym" */
function fmtMins(mins?: number): string {
  if (!mins || isNaN(mins)) return "-";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

/**
 * Build a full ISO string from a base date "YYYY-MM-DD" + time-with-tz "HH:MM:SS±HH:MM"
 * and an optional day adjustment (arrival dateAdjustment).
 */
function buildISO(
  baseDateStr: string | undefined,
  timeWithTz: string | undefined,
  dayAdj: number = 0
): string | null {
  if (!baseDateStr || !timeWithTz) return null;
  try {
    const [y, mo, d] = baseDateStr.split("-").map(Number);
    const base = new Date(Date.UTC(y, mo - 1, d + dayAdj));
    const yy = base.getUTCFullYear();
    const mm = String(base.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(base.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}T${timeWithTz}`;
  } catch {
    return null;
  }
}

/** Compute layover duration string between two ISO strings */
function computeLayoverStr(arrISO: string | null, depISO: string | null): string {
  if (!arrISO || !depISO) return "";
  const a = new Date(arrISO);
  const b = new Date(depISO);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return "";
  const mins = Math.round((b.getTime() - a.getTime()) / 60000);
  if (mins <= 0) return "";
  return fmtMins(mins);
}

/** Format "YYYY-MM-DD" or ISO as "21 Mar 2026" */
function fmtDate(isoStr?: string | null): string {
  if (!isoStr) return "";
  const d = new Date(isoStr.length === 10 ? isoStr + "T00:00:00Z" : isoStr);
  if (isNaN(d.getTime())) return isoStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
  });
}

/** Get departure date string "YYYY-MM-DD" from booking revalidation snapshot */
function getDepDate(booking: any): string | undefined {
  return booking.revalidationSnapshot?.groupedItineraryResponse
    ?.itineraryGroups?.[0]?.groupDescription?.legDescriptions?.[0]?.departureDate;
}

// ── Types ────────────────────────────────────────────────────────────────────
type BookingEligibility = {
  canCancel: boolean;
  canExchange: boolean;
  cancellationPenalties?: { amount?: string; currencyCode?: string; conditionsApply: boolean }[];
  exchangePenalties?: { amount?: string; currencyCode?: string; conditionsApply: boolean }[];
  lastUpdatedAt: string;
};

type Booking = {
  bookingId: string;
  finalBooking: any;
  paymentStatus: string;
  ticketingStatus?: string;
  pnr?: string | null;
  createdAt: string;
  revalidatedData?: any;
  bookingEligibility?: BookingEligibility;
  cancellationStatus?: "none" | "void" | "partial" | "full" | "refund_requested" | "refunded";
};

type CancelType = "void" | "full" | "partial" | "refund";

const CANCEL_STATUS_BADGE: Record<string, { label: string; className: string }> = {
  void: { label: "Voided", className: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  partial: { label: "Partially Cancelled", className: "bg-orange-100 text-orange-800 border-orange-300" },
  full: { label: "Cancelled", className: "bg-red-100 text-red-800 border-red-300" },
  refund_requested: { label: "Refund Requested", className: "bg-blue-100 text-blue-800 border-blue-300" },
  refunded: { label: "Refunded", className: "bg-green-100 text-green-800 border-green-300" },
};

// ── Component ────────────────────────────────────────────────────────────────
const BookingHistory = () => {
  const [user] = useAuthState(auth);
  // Staff only. A customer cannot change their own booking's requests in phase
  // 1 — they ring the counter, and whoever answers does it for them.
  const { profile } = useAuth();
  const staff = isStaff(profile);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState<"upcoming" | "completed" | "cancelled">("upcoming");
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());

  const [cancelModalBooking, setCancelModalBooking] = useState<Booking | null>(null);
  const [cancelType, setCancelType] = useState<CancelType>("full");
  const [cancelReason, setCancelReason] = useState("");
  const [selectedSegments, setSelectedSegments] = useState<number[]>([]);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);

  // The code that approves this cancellation. Null until one has been asked
  // for, and cleared whenever the action changes — the server fingerprints the
  // exact action, so a code raised for a void is refused against a refund.
  const [cancelOtp, setCancelOtp] = useState<CancelOtpChallenge | null>(null);
  const [cancelOtpCode, setCancelOtpCode] = useState("");
  const [cancelOtpResending, setCancelOtpResending] = useState(false);

  const [exchangeModalBooking, setExchangeModalBooking] = useState<Booking | null>(null);
  const [exchangeNewDate, setExchangeNewDate] = useState("");
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const [exchangeSuccess, setExchangeSuccess] = useState<string | null>(null);
  const [exchangeOffers, setExchangeOffers] = useState<any[] | null>(null);
  const [exchangeRawData, setExchangeRawData] = useState<any>(null);

  const fetchBookings = useCallback(
    async (cursor?: string, append: boolean = false) => {
      if (!user?.uid) return;
      if (!append) { setLoading(true); setError(null); }
      else setLoadingMore(true);
      try {
        const baseUrl = process.env.REACT_APP_API_URL || "";
        const params = new URLSearchParams({ userId: user.uid, limit: "20" });
        if (cursor) params.append("cursor", cursor);
        // The session cookie identifies whose bookings these are — the server
        // no longer takes that from the query string. Without this the request
        // arrives anonymous and is refused, because a bare fetch() does not
        // send cookies cross-origin (the app and the API are different ports).
        const response = await fetch(`${baseUrl}/bookings?${params.toString()}`, {
          credentials: "include",
        });
        if (!response.ok) {
          const errorBody = await response.json().catch(() => null);
          throw new Error(errorBody?.error || "Failed to fetch bookings.");
        }
        const data = await response.json();
        const nextData = Array.isArray(data.bookings) ? data.bookings : [];
        setBookings((prev) => (append ? [...prev, ...nextData] : nextData));
        setHasMore(Boolean(data.pagination?.hasMore));
        setNextCursor(data.pagination?.nextCursor);
      } catch (err: any) {
        console.error("[BookingHistory] Failed to fetch bookings", err);
        setError(err.message || "Unable to load bookings.");
        if (!append) setBookings([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [user?.uid]
  );

  useEffect(() => {
    if (!user) {
      setBookings([]); setHasMore(false);
      setNextCursor(undefined); setLoading(false);
      return;
    }
    fetchBookings();
  }, [user, fetchBookings]);

  const loadMore = () => {
    if (hasMore && nextCursor && !loadingMore) fetchBookings(nextCursor, true);
  };

  const isPastBooking = (booking: any) => {
    const depDate = getDepDate(booking);
    if (depDate) return new Date(depDate + "T00:00:00Z") < new Date();
    const oldDate = booking.finalBooking?.endDate ?? null;
    if (!oldDate) return false;
    return new Date(oldDate) < new Date();
  };

  const toggleExpanded = (bookingId: string) => {
    setExpandedBookings((prev) => {
      const next = new Set(prev);
      if (next.has(bookingId)) next.delete(bookingId);
      else next.add(bookingId);
      return next;
    });
  };

  // ── Cancel helpers ──────────────────────────────────────────────────────────
  const openCancelModal = (booking: Booking) => {
    setCancelModalBooking(booking);
    setCancelType("full");
    setCancelReason("");
    setSelectedSegments([]);
    setCancelError(null);
    setCancelSuccess(null);
    setCancelOtp(null);
    setCancelOtpCode("");
  };
  const closeCancelModal = () => {
    setCancelModalBooking(null);
    setCancelError(null);
    setCancelSuccess(null);
    setCancelOtp(null);
    setCancelOtpCode("");
  };
  /** Any change to what is being cancelled invalidates a code already sent. */
  const discardCancelOtp = () => { setCancelOtp(null); setCancelOtpCode(""); };
  const toggleSegment = (segNum: number) => {
    discardCancelOtp();
    return setSelectedSegments((prev) =>
      prev.includes(segNum) ? prev.filter((s) => s !== segNum) : [...prev, segNum]
    );
  };

  // ── Exchange helpers ────────────────────────────────────────────────────────
  const openExchangeModal = (booking: Booking) => {
    setExchangeModalBooking(booking);
    setExchangeNewDate("");
    setExchangeError(null);
    setExchangeSuccess(null);
    setExchangeOffers(null);
    setExchangeRawData(null);
  };
  const closeExchangeModal = () => {
    setExchangeModalBooking(null);
    setExchangeError(null);
    setExchangeSuccess(null);
  };

  const handleExchangeSearch = async () => {
    if (!exchangeModalBooking || !exchangeNewDate) return;
    setExchangeLoading(true);
    setExchangeError(null);
    setExchangeOffers(null);
    try {
      const result = await searchExchangeOffers({
        pnr: exchangeModalBooking.pnr!,
        newDepartureDate: exchangeNewDate,
      });
      if (result.success) {
        setExchangeOffers(result.offers ?? []);
        setExchangeRawData(result.rawData);
      } else {
        setExchangeError(result.error || "No exchange options available.");
      }
    } catch (err: any) {
      setExchangeError(
        err?.response?.data?.errors?.[0]?.message ||
        err?.response?.data?.error ||
        err?.message || "Failed to search exchange options."
      );
    } finally {
      setExchangeLoading(false);
    }
  };

  /**
   * The exact action being cancelled.
   *
   * Built once and used for both the code request and the cancellation itself,
   * because the server fingerprints these fields — if the two calls disagree by
   * so much as one ticket number, the code is refused.
   *
   * Returns null when the action is incomplete; the error is already on screen.
   */
  const buildCancelPayload = (): any | null => {
    if (!cancelModalBooking || !user?.uid) return null;
    const booking = cancelModalBooking;
    const ticketNumbers: string[] =
      booking.finalBooking?.flightTickets?.map((t: any) => t.number).filter(Boolean) ?? [];
    const payload: any = {
      pnr: booking.pnr, status: cancelType,
      userId: user.uid, bookingId: booking.bookingId,
    };
    if (cancelReason.trim()) payload.reason = cancelReason.trim();
    if (cancelType === "void") {
      if (!ticketNumbers.length) { setCancelError("No ticket numbers found for void."); return null; }
      payload.ticketNumbers = ticketNumbers;
    } else if (cancelType === "full") {
      if (ticketNumbers.length) payload.ticketNumbers = ticketNumbers;
    } else if (cancelType === "partial") {
      if (!selectedSegments.length) { setCancelError("Please select at least one segment."); return null; }
      payload.segmentNumbers = selectedSegments;
    } else if (cancelType === "refund") {
      if (!ticketNumbers.length) { setCancelError("No ticket numbers found for refund."); return null; }
      payload.ticketNumbers = [ticketNumbers[0]];
    }
    return payload;
  };

  const readError = (err: any, fallback: string) =>
    err?.response?.data?.errors?.[0]?.message ||
    err?.response?.data?.message ||
    err?.message || fallback;

  /**
   * Two presses, not one, once cancellation codes are on.
   *
   * The first asks for a code and stops. The second spends it. Staff who do not
   * need a code — and every caller while the server has them switched off — get
   * `otpRequired: false` and fall straight through to the cancellation, so this
   * stays a single press for them.
   */
  const handleCancelConfirm = async () => {
    const payload = buildCancelPayload();
    if (!payload) return;

    setCancelLoading(true); setCancelError(null); setCancelSuccess(null);
    try {
      if (!cancelOtp) {
        const challenge = await requestCancelOtp({
          bookingId: payload.bookingId,
          status: payload.status,
          ticketNumbers: payload.ticketNumbers,
          segmentNumbers: payload.segmentNumbers,
        });
        if (challenge?.otpRequired) {
          setCancelOtp(challenge);
          setCancelOtpCode("");
          return;
        }
        // No code needed — carry on and cancel.
      } else {
        const code = cancelOtpCode.trim();
        if (!code) { setCancelError("Enter the code sent to your phone."); return; }
        payload.challengeId = cancelOtp.challengeId;
        payload.code = code;
      }

      const result = await cancelBooking(payload);
      setCancelSuccess(result.message || "Booking cancelled successfully.");
      setTimeout(() => { closeCancelModal(); fetchBookings(); }, 2000);
    } catch (err: any) {
      setCancelError(readError(err, "Failed to cancel booking."));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCancelOtpResend = async () => {
    const payload = buildCancelPayload();
    if (!payload || !cancelOtp?.challengeId) return;
    setCancelOtpResending(true); setCancelError(null);
    try {
      const challenge = await resendCancelOtp({
        bookingId: payload.bookingId,
        status: payload.status,
        ticketNumbers: payload.ticketNumbers,
        segmentNumbers: payload.segmentNumbers,
        challengeId: cancelOtp.challengeId,
      });
      if (challenge?.challengeId) setCancelOtp(challenge);
    } catch (err: any) {
      setCancelError(readError(err, "Could not send the code again."));
    } finally {
      setCancelOtpResending(false);
    }
  };

  // ── Tab filtering ──────────────────────────────────────────────────────────
  const isCancelled = (b: Booking) => b.cancellationStatus && b.cancellationStatus !== "none";
  const upcomingBookings = bookings.filter((b) => !isCancelled(b) && !isPastBooking(b));
  const completedBookings = bookings.filter((b) => !isCancelled(b) && isPastBooking(b));
  const cancelledBookings = bookings.filter((b) => isCancelled(b));

  const filteredBookings =
    activeTab === "upcoming" ? upcomingBookings :
    activeTab === "completed" ? completedBookings :
    cancelledBookings;

  // ── Loading / error states ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="text-gray-500 text-center">
          <FaPlane className="text-primary text-4xl mx-auto mb-4 animate-pulse" />
          <p className="text-lg font-medium">Loading your trips...</p>
        </div>
      </div>
    );
  }
  if (error && !bookings.length) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="text-red-500 text-center">
          <FaTimesCircle className="text-4xl mx-auto mb-3" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: "upcoming" as const, label: "Upcoming", count: upcomingBookings.length },
    { key: "completed" as const, label: "Completed", count: completedBookings.length },
    { key: "cancelled" as const, label: "Cancelled", count: cancelledBookings.length },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-8">

        {/* ── Page Header ── */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <FaPlaneDeparture className="text-primary text-2xl" />
            <h1 className="text-2xl font-bold text-gray-900">My Trips</h1>
          </div>
          <p className="text-sm text-gray-500 ml-10">Manage your bookings</p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ── Tab Bar ── */}
        <div className="bg-white rounded-t-xl border-b border-gray-200 shadow-sm mb-0">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`relative flex-1 sm:flex-none px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? "text-primary font-bold"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold ${
                    activeTab === tab.key
                      ? "bg-primary/10 text-primary"
                      : "bg-gray-100 text-gray-500"
                  }`}>
                    {tab.count}
                  </span>
                )}
                {activeTab === tab.key && (
                  <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary rounded-t-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Booking Cards ── */}
        {filteredBookings.length === 0 ? (
          <div className="bg-white rounded-b-xl shadow-sm py-20 text-center">
            {activeTab === "upcoming" && (
              <>
                <FaPlaneDeparture className="text-gray-200 text-6xl mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium mb-1">No upcoming trips</p>
                <p className="text-gray-400 text-sm">Start planning your next adventure!</p>
              </>
            )}
            {activeTab === "completed" && (
              <>
                <FaCheckCircle className="text-gray-200 text-6xl mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium mb-1">No completed trips yet</p>
                <p className="text-gray-400 text-sm">Your past trips will appear here.</p>
              </>
            )}
            {activeTab === "cancelled" && (
              <>
                <FaTimesCircle className="text-gray-200 text-6xl mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium mb-1">No cancelled bookings</p>
                <p className="text-gray-400 text-sm">Cancelled or voided bookings appear here.</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {filteredBookings.map((booking: any) => {
              // ── Extract data ──
              const schedules: any[] = booking.revalidationResponse?.onward?.schedules ?? [];
              const departureDate = getDepDate(booking);
              const onward = booking.revalidationResponse?.onward;

              const pricingInfo = booking.revalidationSnapshot
                ?.groupedItineraryResponse?.itineraryGroups?.[0]
                ?.itineraries?.[0]?.pricingInformation?.[0];
              const totalFareInfo = pricingInfo?.fare?.totalFare;

              const passengers: any[] = booking.passengerData ?? [];
              const depAirport = onward?.departureAirport ?? schedules[0]?.departure?.airport ?? "--";
              const arrAirport = onward?.arrivalAirport ?? schedules[schedules.length - 1]?.arrival?.airport ?? "--";
              const depCity = schedules[0]?.departure?.city ?? "";
              const arrCity = schedules[schedules.length - 1]?.arrival?.city ?? "";

              const ticketingStatus = booking.ticketingStatus;
              const isPast = isPastBooking(booking);
              const isExpanded = expandedBookings.has(booking.bookingId);
              const eligibility = booking.bookingEligibility;
              const cancelled = isCancelled(booking);
              const canCancel = !cancelled && !isPast && ticketingStatus === "success";
              const canExchange = !cancelled && !isPast && ticketingStatus === "success";

              // Compute total duration from first dep to last arr
              const firstSeg = schedules[0];
              const lastSeg = schedules[schedules.length - 1];
              const firstDepTime = extractHHMM(firstSeg?.departure?.time);
              const lastArrTime = extractHHMM(lastSeg?.arrival?.time);
              const lastArrAdj = lastSeg?.arrival?.dateAdjustment ?? 0;
              const totalElapsed = schedules.reduce((sum: number, s: any) => sum + (s.elapsedTime || 0), 0);
              // Add layover durations
              let totalWithLayovers = totalElapsed;
              for (let i = 1; i < schedules.length; i++) {
                const prev = schedules[i - 1];
                const curr = schedules[i];
                const prevArrISO = buildISO(departureDate, prev.arrival?.time, prev.arrival?.dateAdjustment ?? 0);
                const currDepISO = buildISO(departureDate, curr.departure?.time, 0);
                if (prevArrISO && currDepISO) {
                  const layoverMins = Math.round((new Date(currDepISO).getTime() - new Date(prevArrISO).getTime()) / 60000);
                  if (layoverMins > 0) totalWithLayovers += layoverMins;
                }
              }

              const totalStops = schedules.length > 1 ? schedules.length - 1 : 0;
              const firstAirlineCode = firstSeg?.flightCode ?? firstSeg?.operatingCarrier ?? "";

              // Status badge
              let statusLabel = "";
              let statusColor = "";
              if (cancelled && booking.cancellationStatus && CANCEL_STATUS_BADGE[booking.cancellationStatus]) {
                statusLabel = CANCEL_STATUS_BADGE[booking.cancellationStatus].label;
                statusColor = CANCEL_STATUS_BADGE[booking.cancellationStatus].className;
              } else if (ticketingStatus === "success") {
                statusLabel = "Confirmed";
                statusColor = "bg-green-50 text-green-700 border-green-200";
              } else if (ticketingStatus === "failed") {
                statusLabel = "Failed";
                statusColor = "bg-red-50 text-red-700 border-red-200";
              } else {
                statusLabel = "Pending";
                statusColor = "bg-yellow-50 text-yellow-700 border-yellow-200";
              }

              // Ticket numbers from finalBooking
              const flightTickets: any[] = booking.finalBooking?.flightTickets ?? [];

              return (
                <div
                  key={booking.bookingId}
                  className={`bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 ${
                    cancelled ? "opacity-85" : ""
                  }`}
                >
                  {/* ── Card Header: Airline + PNR + Status ── */}
                  <div className="px-5 pt-5 pb-4">
                    <div className="flex items-start justify-between gap-4 mb-5">
                      {/* Airline info */}
                      <div className="flex items-center gap-3">
                        <img
                          src={`/assets/airlines/${firstAirlineCode}.png`}
                          alt={firstAirlineCode}
                          className="w-10 h-10 object-contain rounded"
                          onError={(e) =>
                            ((e.target as HTMLImageElement).src =
                              `https://via.placeholder.com/40x40?text=${firstAirlineCode}`)
                          }
                        />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {AIRLINE_NAMES[firstAirlineCode] ?? firstAirlineCode}
                          </div>
                          <div className="text-xs text-gray-400">
                            {firstAirlineCode} {firstSeg?.flightNumber}
                            {schedules.length > 1 && ` + ${schedules.length - 1} more`}
                          </div>
                        </div>
                      </div>

                      {/* PNR + Status */}
                      <div className="flex items-center gap-3 shrink-0">
                        {booking.pnr && (
                          <div className="text-right">
                            <div className="text-[10px] text-gray-400 uppercase tracking-wider">PNR</div>
                            <div className="font-mono text-base font-bold text-primary tracking-widest">
                              {booking.pnr}
                            </div>
                          </div>
                        )}
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold border ${statusColor}`}>
                          {statusLabel}
                        </span>
                      </div>
                    </div>

                    {/* ── Flight Route Visualization ── */}
                    {schedules.length > 0 ? (
                      <div>
                        {/* Main route line */}
                        <div className="flex items-center gap-3 sm:gap-5">
                          {/* Departure */}
                          <div className="shrink-0 text-left">
                            <div className="text-2xl font-bold text-gray-900 tabular-nums leading-none">
                              {firstDepTime}
                            </div>
                            <div className="text-base font-bold text-gray-800 mt-1">{depAirport}</div>
                            <div className="text-xs text-gray-400">{depCity}</div>
                          </div>

                          {/* Flight line */}
                          <div className="flex-1 flex flex-col items-center min-w-0 px-2">
                            <div className="text-xs text-gray-500 font-medium mb-1.5">
                              {fmtMins(totalWithLayovers)}
                            </div>
                            <div className="w-full flex items-center relative">
                              <div className="w-2 h-2 rounded-full bg-gray-400 shrink-0" />
                              <div className="flex-1 border-t-2 border-gray-300 relative">
                                <FaPlane size={14} className="text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                              </div>
                              <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[8px] border-l-gray-400 shrink-0" />
                            </div>
                            <div className="text-[11px] text-gray-400 mt-1.5">
                              {totalStops === 0 ? "Non-stop" : `${totalStops} stop${totalStops > 1 ? "s" : ""}`}
                            </div>
                          </div>

                          {/* Arrival */}
                          <div className="shrink-0 text-right">
                            <div className="text-2xl font-bold text-gray-900 tabular-nums leading-none">
                              {lastArrTime}
                              {lastArrAdj > 0 && (
                                <span className="text-xs text-orange-500 align-super ml-0.5">+{lastArrAdj}</span>
                              )}
                            </div>
                            <div className="text-base font-bold text-gray-800 mt-1">{arrAirport}</div>
                            <div className="text-xs text-gray-400">{arrCity}</div>
                          </div>
                        </div>

                        {/* Date row */}
                        {departureDate && (
                          <div className="flex items-center justify-center gap-2 mt-3">
                            <FaCalendarAlt size={11} className="text-gray-400" />
                            <span className="text-xs text-gray-500 font-medium">{fmtDate(departureDate)}</span>
                          </div>
                        )}

                        {/* Layover indicators (for connecting flights) */}
                        {schedules.length > 1 && (
                          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                            {schedules.slice(0, -1).map((seg: any, i: number) => {
                              const nextSeg = schedules[i + 1];
                              const prevArrAdj = seg.arrival?.dateAdjustment ?? 0;
                              const prevArrISO = buildISO(departureDate, seg.arrival?.time, prevArrAdj);
                              const nextDepISO = buildISO(departureDate, nextSeg?.departure?.time, 0);
                              const layoverStr = computeLayoverStr(prevArrISO, nextDepISO);
                              return (
                                <div key={i} className="flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1 rounded-full">
                                  <FaClock size={10} className="shrink-0" />
                                  <span>
                                    {layoverStr && <span className="font-semibold">{layoverStr}</span>}
                                    {" "}layover at{" "}
                                    <span className="font-semibold">{seg.arrival?.airport ?? nextSeg?.departure?.airport}</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="py-6 text-sm text-gray-400 text-center">
                        Flight details not available for this booking.
                      </div>
                    )}
                  </div>

                  {/* ── View Details Toggle ── */}
                  <div
                    className="px-5 py-3 border-t border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 select-none"
                    onClick={() => toggleExpanded(booking.bookingId)}
                  >
                    {isExpanded ? (
                      <FaChevronUp size={12} className="text-primary" />
                    ) : (
                      <FaChevronDown size={12} className="text-primary" />
                    )}
                    <span className="text-sm font-semibold text-primary">
                      {isExpanded ? "Hide Details" : "View Details"}
                    </span>
                  </div>

                  {/* ── Expanded Details ── */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50/50">
                      {/* Special requests — only once a PNR exists, because
                          there is nothing to attach one to before that. */}
                      {staff && booking.pnr && (
                        <SpecialRequestsPanel bookingId={booking.bookingId} />
                      )}

                      {/* Passengers */}
                      {passengers.length > 0 && (
                        <div className="px-5 py-4 border-b border-gray-100">
                          <div className="flex items-center gap-2 mb-3">
                            <FaUser size={12} className="text-primary" />
                            <h4 className="text-sm font-bold text-gray-800">Passengers</h4>
                          </div>
                          <div className="space-y-2">
                            {passengers.map((p: any, i: number) => {
                              // Find matching ticket
                              const ticket = flightTickets.find(
                                (t: any) => t.passengerName?.toLowerCase().includes(p.lastName?.toLowerCase())
                              );
                              return (
                                <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-white rounded-lg px-4 py-2.5 border border-gray-100">
                                  <span className="font-semibold text-sm text-gray-900">
                                    {p.title} {p.firstName} {p.lastName}
                                  </span>
                                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded uppercase font-medium">
                                    {p.passengerType === "ADT" ? "Adult" : p.passengerType === "CHD" ? "Child" : p.passengerType === "INF" ? "Infant" : p.passengerType}
                                  </span>
                                  {p.passportNumber && (
                                    <span className="text-xs text-gray-500">Passport: {p.passportNumber}</span>
                                  )}
                                  {p.dob && (
                                    <span className="text-xs text-gray-500">DOB: {p.dob}</span>
                                  )}
                                  {ticket?.number && (
                                    <span className="text-xs text-gray-500 font-mono">Ticket: {ticket.number}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Fare Breakdown */}
                      {totalFareInfo && (
                        <div className="px-5 py-4 border-b border-gray-100">
                          <div className="flex items-center gap-2 mb-3">
                            <FaDownload size={12} className="text-primary" />
                            <h4 className="text-sm font-bold text-gray-800">Fare Breakdown</h4>
                          </div>
                          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                            <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-50">
                              <span className="text-sm text-gray-600">Base Fare</span>
                              <span className="text-sm font-medium text-gray-800">
                                {totalFareInfo.baseFareCurrency} {totalFareInfo.baseFareAmount?.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-50">
                              <span className="text-sm text-gray-600">Taxes & Fees</span>
                              <span className="text-sm font-medium text-gray-800">
                                {totalFareInfo.currency} {totalFareInfo.totalTaxAmount?.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between items-center px-4 py-2.5 bg-primary/5">
                              <span className="text-sm font-bold text-gray-900">Total</span>
                              <span className="text-sm font-bold text-primary">
                                {totalFareInfo.currency} {totalFareInfo.totalPrice?.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Flight Segment Details */}
                      {schedules.length > 0 && (
                        <div className="px-5 py-4 border-b border-gray-100">
                          <div className="flex items-center gap-2 mb-3">
                            <FaPlane size={12} className="text-primary" />
                            <h4 className="text-sm font-bold text-gray-800">Flight Details</h4>
                          </div>
                          <div className="space-y-3">
                            {schedules.map((seg: any, i: number) => {
                              const airlineCode = seg.flightCode ?? seg.operatingCarrier ?? "";
                              const operatingCode = seg.operatingCarrier ?? airlineCode;
                              const isCodeshare = operatingCode && operatingCode !== airlineCode;
                              return (
                                <div key={i} className="bg-white rounded-lg border border-gray-100 px-4 py-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={`/assets/airlines/${airlineCode}.png`}
                                        alt={airlineCode}
                                        className="w-6 h-6 object-contain"
                                        onError={(e) =>
                                          ((e.target as HTMLImageElement).src =
                                            `https://via.placeholder.com/24x24?text=${airlineCode}`)
                                        }
                                      />
                                      <span className="text-sm font-semibold text-gray-800">
                                        {AIRLINE_NAMES[airlineCode] ?? airlineCode} {airlineCode} {seg.flightNumber}
                                      </span>
                                      {isCodeshare && (
                                        <span className="text-xs text-gray-400">Operated by {AIRLINE_NAMES[operatingCode] ?? operatingCode}</span>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-500">{fmtMins(seg.elapsedTime)}</span>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
                                    <span className="font-medium">{extractHHMM(seg.departure?.time)} {seg.departure?.airport}</span>
                                    <FaPlane size={10} className="text-gray-300" />
                                    <span className="font-medium">
                                      {extractHHMM(seg.arrival?.time)} {seg.arrival?.airport}
                                      {(seg.arrival?.dateAdjustment ?? 0) > 0 && (
                                        <span className="text-orange-500 ml-1">+{seg.arrival.dateAdjustment}</span>
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-3 text-xs">
                                    <span className="flex items-center gap-1 text-gray-500">
                                      <FaSuitcase size={10} />
                                      {CABIN_NAMES[seg.cabinCode ?? ""] ?? seg.cabinCode ?? "Economy"}
                                    </span>
                                    {seg.baggage && (
                                      <span className="flex items-center gap-1 text-gray-500">
                                        <FaLuggageCart size={10} />
                                        {seg.baggage}
                                      </span>
                                    )}
                                    {seg.mealCode === "M" && (
                                      <span className="text-green-600">Meal included</span>
                                    )}
                                    {seg.equipment && (
                                      <span className="text-gray-400">Aircraft: {seg.equipment}</span>
                                    )}
                                    {seg.seatsAvailable != null && (
                                      <span className="text-orange-500">{seg.seatsAvailable} seats left</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Ticket Numbers */}
                      {flightTickets.length > 0 && (
                        <div className="px-5 py-4 border-b border-gray-100">
                          <div className="flex items-center gap-2 mb-3">
                            <FaDownload size={12} className="text-primary" />
                            <h4 className="text-sm font-bold text-gray-800">Ticket Numbers</h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {flightTickets.map((ticket: any, i: number) => (
                              <div key={i} className="bg-white rounded-lg border border-gray-100 px-4 py-2 text-sm">
                                <span className="text-gray-400 text-xs">#{i + 1}</span>{" "}
                                <span className="font-mono font-semibold text-gray-800">{ticket.number}</span>
                                {ticket.passengerName && (
                                  <span className="text-gray-500 ml-2 text-xs">- {ticket.passengerName}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Agency & Report Reference */}
                      {(() => {
                        const fb = booking.finalBooking;
                        const creation = fb?.creationDetails;
                        const firstTicket = flightTickets[0];
                        const accounting = fb?.accountingItems;
                        const firstAccounting = Array.isArray(accounting) ? accounting[0] : null;

                        const agentSine = creation?.creationUserSine;
                        const pccCode = creation?.userWorkPcc || firstTicket?.ticketingPcc;
                        const iataNumber = firstTicket?.agencyIataNumber;
                        const ticketingAirline = firstTicket?.airlineCode;
                        const commissionAmt = firstAccounting?.commission?.commissionAmount;

                        const hasData = agentSine || pccCode || iataNumber || ticketingAirline;

                        return hasData ? (
                          <div className="px-5 py-4 border-b border-gray-100">
                            <div className="flex items-center gap-2 mb-3">
                              <FaClipboardList size={12} className="text-primary" />
                              <h4 className="text-sm font-bold text-gray-800">Agency & Report Info</h4>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-gray-100">
                                {ticketingAirline && (
                                  <div className="bg-white px-4 py-3">
                                    <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Ticketing Provider</div>
                                    <div className="text-sm font-bold text-gray-900 font-mono">{ticketingAirline}</div>
                                    <div className="text-[10px] text-gray-400">{AIRLINE_NAMES[ticketingAirline] ?? ""}</div>
                                  </div>
                                )}
                                {agentSine && (
                                  <div className="bg-white px-4 py-3">
                                    <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Agent Sine / Employee</div>
                                    <div className="text-sm font-bold text-gray-900 font-mono">{agentSine}</div>
                                  </div>
                                )}
                                {pccCode && (
                                  <div className="bg-white px-4 py-3">
                                    <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">PCC / Station</div>
                                    <div className="text-sm font-bold text-gray-900 font-mono">{pccCode}</div>
                                  </div>
                                )}
                                {iataNumber && (
                                  <div className="bg-white px-4 py-3">
                                    <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Agency IATA</div>
                                    <div className="text-sm font-bold text-gray-900 font-mono">{iataNumber}</div>
                                  </div>
                                )}
                                {commissionAmt != null && (
                                  <div className="bg-white px-4 py-3">
                                    <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Commission</div>
                                    <div className="text-sm font-bold text-gray-900">{commissionAmt}</div>
                                  </div>
                                )}
                                {creation?.creationDate && (
                                  <div className="bg-white px-4 py-3">
                                    <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Ticketing Date</div>
                                    <div className="text-sm font-bold text-gray-900">{creation.creationDate} {creation.creationTime ?? ""}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-2">
                              Use Ticketing Provider + Agent Sine + PCC to search in Daily Sales Report and ASR reports.
                            </p>
                          </div>
                        ) : null;
                      })()}

                      {/* Cancellation Info */}
                      {eligibility && (eligibility.cancellationPenalties?.length ?? 0) > 0 && (
                        <div className="px-5 py-4 border-b border-gray-100">
                          <div className="flex items-center gap-2 mb-3">
                            <FaTimesCircle size={12} className="text-primary" />
                            <h4 className="text-sm font-bold text-gray-800">Cancellation Info</h4>
                          </div>
                          <div className="bg-white rounded-lg border border-gray-100 px-4 py-3">
                            <ul className="space-y-1">
                              {eligibility.cancellationPenalties!.map((p: any, i: number) => (
                                <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" />
                                  Penalty: {p.amount} {p.currencyCode}
                                  {p.conditionsApply && <span className="text-xs text-gray-400">(conditions apply)</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Card Footer: Price + Booking ID + Actions ── */}
                  <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-5 flex-wrap">
                      {/* Total */}
                      {totalFareInfo && (
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-xs text-gray-400 font-medium">Total:</span>
                          <span className="text-lg font-bold text-primary tabular-nums">
                            {totalFareInfo.currency} {totalFareInfo.totalPrice?.toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="w-px h-6 bg-gray-200 hidden sm:block" />
                      {/* Booking ID */}
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-xs text-gray-400 font-medium">Booking ID:</span>
                        <span className="text-xs text-gray-600 font-mono">{booking.bookingId}</span>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      {/* Cancel appears with ticketChangesEnabled; Exchange has
                          its own switch because reissues are not ready. The
                          server refuses both routes regardless, so this only
                          keeps the screen honest about what it can do. */}
                      {!cancelled && ticketChangesEnabled && (
                        <>
                          <button
                            type="button"
                            onClick={() => openCancelModal(booking)}
                            disabled={!canCancel}
                            className={`flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-semibold transition ${
                              canCancel
                                ? "border-red-300 text-red-600 hover:bg-red-50"
                                : "border-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                          >
                            <FaTimesCircle size={12} />
                            Cancel
                          </button>
                          {exchangeEnabled && (
                            <button
                              type="button"
                              onClick={() => openExchangeModal(booking)}
                              disabled={!canExchange}
                              className={`flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-semibold transition ${
                                canExchange
                                  ? "border-primary text-primary hover:bg-primary/10"
                                  : "border-gray-200 text-gray-400 cursor-not-allowed"
                              }`}
                            >
                              <FaExchangeAlt size={12} />
                              Exchange
                            </button>
                          )}
                        </>
                      )}
                      {cancelled && booking.cancellationStatus && CANCEL_STATUS_BADGE[booking.cancellationStatus] && (
                        <span className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${CANCEL_STATUS_BADGE[booking.cancellationStatus].className}`}>
                          {CANCEL_STATUS_BADGE[booking.cancellationStatus].label}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="px-6 py-2.5 bg-primary text-white rounded-lg font-semibold hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loadingMore ? "Loading..." : "Load More Trips"}
            </button>
          </div>
        )}
      </div>

      {/* ── Cancel Modal ─────────────────────────────────────────────────────── */}
      {cancelModalBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 relative max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Cancel Booking</h2>
                <p className="text-sm text-gray-500">
                  PNR: <span className="font-semibold font-mono text-primary">{cancelModalBooking.pnr || "N/A"}</span>
                </p>
              </div>
              <button
                onClick={closeCancelModal}
                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                aria-label="Close"
              >
                <FaTimesCircle size={18} />
              </button>
            </div>

            <div className="px-6 py-5">
              {cancelModalBooking.bookingEligibility?.cancellationPenalties &&
                cancelModalBooking.bookingEligibility.cancellationPenalties.length > 0 && (
                  <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    <strong>Cancellation Penalties:</strong>
                    <ul className="mt-1 list-disc list-inside">
                      {cancelModalBooking.bookingEligibility.cancellationPenalties.map((p, i) => (
                        <li key={i}>
                          {p.amount} {p.currencyCode}
                          {p.conditionsApply ? " (conditions apply)" : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cancellation Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { value: "void" as CancelType, label: "Void", desc: "Same-day, no penalty" },
                    { value: "full" as CancelType, label: "Full Cancel", desc: "Cancel all segments" },
                    (cancelModalBooking.finalBooking?.flights?.length > 1)
                      ? { value: "partial" as CancelType, label: "Partial Cancel", desc: "Cancel specific segments" }
                      : null,
                    refundEnabled
                      ? { value: "refund" as CancelType, label: "Refund", desc: "Request ticket refund" }
                      : null,
                  ].filter(Boolean) as { value: CancelType; label: string; desc: string }[]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setCancelType(opt.value); setCancelError(null); discardCancelOtp(); }}
                      className={`rounded-lg border px-3 py-2.5 text-left text-sm transition ${
                        cancelType === opt.value
                          ? "border-primary bg-primary/10 text-primary font-semibold ring-1 ring-primary/30"
                          : "border-gray-200 text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      <span className="block font-semibold">{opt.label}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {cancelType === "partial" && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Select Segments to Cancel</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(cancelModalBooking.finalBooking?.flights ?? []).map((flight: any, idx: number) => {
                      const segNum = idx + 1;
                      return (
                        <label
                          key={segNum}
                          className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition ${
                            selectedSegments.includes(segNum)
                              ? "border-primary bg-primary/5"
                              : "border-gray-200 hover:border-gray-400"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSegments.includes(segNum)}
                            onChange={() => toggleSegment(segNum)}
                            className="accent-primary w-4 h-4"
                          />
                          <span className="text-sm">
                            <span className="font-semibold">Segment {segNum}:</span>{" "}
                            {flight.fromAirportCode || flight.departureAirport} →{" "}
                            {flight.toAirportCode || flight.arrivalAirport}
                            {flight.airlineCode && ` (${flight.airlineCode} ${flight.flightNumber || ""})`}
                          </span>
                        </label>
                      );
                    })}
                    {!cancelModalBooking.finalBooking?.flights?.length && (
                      <p className="text-sm text-gray-400">No flight segments available.</p>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Reason <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={2}
                  placeholder="Why are you cancelling?"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
                />
              </div>

              {/* Shown only once a code has actually been sent. Staff who do not
                  need one never see this step. */}
              {cancelOtp && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="text-sm font-semibold text-amber-900">
                    Enter the code to approve {cancelOtp.amount || "this cancellation"}
                  </div>
                  <div className="mt-1 text-xs text-amber-800">
                    Sent to {cancelOtp.sentTo || "your phone"}. The code approves this exact
                    action — change what you are cancelling and you will need a new one.
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={cancelOtpCode}
                      onChange={(e) => setCancelOtpCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="Code"
                      className="w-32 rounded-lg border border-amber-300 px-3 py-2 text-sm tracking-[0.3em] font-mono focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleCancelOtpResend}
                      disabled={cancelOtpResending || cancelLoading}
                      className="text-xs font-semibold text-amber-900 underline disabled:opacity-50"
                    >
                      {cancelOtpResending ? "Sending…" : "Send again"}
                    </button>
                  </div>
                </div>
              )}

              {cancelError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{cancelError}</div>
              )}
              {cancelSuccess && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{cancelSuccess}</div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button
                type="button"
                onClick={closeCancelModal}
                disabled={cancelLoading}
                className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleCancelConfirm}
                disabled={cancelLoading || !!cancelSuccess}
                className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {cancelLoading
                  ? "Processing…"
                  : cancelOtp ? "Confirm with code" : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Exchange Modal ───────────────────────────────────────────────────── */}
      {exchangeModalBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 relative max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Exchange Booking</h2>
                <p className="text-sm text-gray-500">
                  PNR: <span className="font-semibold font-mono text-primary">{exchangeModalBooking.pnr || "N/A"}</span>
                </p>
              </div>
              <button
                onClick={closeExchangeModal}
                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                aria-label="Close"
              >
                <FaTimesCircle size={18} />
              </button>
            </div>

            <div className="px-6 py-5">
              {/* Exchange Penalties Info */}
              {exchangeModalBooking.bookingEligibility?.exchangePenalties &&
                exchangeModalBooking.bookingEligibility.exchangePenalties.length > 0 && (
                  <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    <strong>Exchange Penalties:</strong>
                    <ul className="mt-1 list-disc list-inside">
                      {exchangeModalBooking.bookingEligibility.exchangePenalties.map((p, i) => (
                        <li key={i}>
                          {p.amount} {p.currencyCode}
                          {p.conditionsApply ? " (conditions apply)" : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* New Departure Date */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Departure Date</label>
                <input
                  type="date"
                  value={exchangeNewDate}
                  onChange={(e) => setExchangeNewDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                />
              </div>

              {/* Search Button */}
              <button
                type="button"
                onClick={handleExchangeSearch}
                disabled={exchangeLoading || !exchangeNewDate}
                className="w-full rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition mb-4"
              >
                {exchangeLoading ? "Searching..." : "Search Alternatives"}
              </button>

              {/* Error */}
              {exchangeError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{exchangeError}</div>
              )}

              {/* Success */}
              {exchangeSuccess && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{exchangeSuccess}</div>
              )}

              {/* Results */}
              {exchangeOffers !== null && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-gray-800 mb-2">Available Alternatives</h4>
                  {exchangeOffers.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                      No alternative flights found for the selected date.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {exchangeOffers.map((offer: any, i: number) => (
                        <div key={i} className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm">
                          <div className="font-semibold text-gray-800 mb-1">Option {i + 1}</div>
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                            {JSON.stringify(offer, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Raw Response Data (collapsible, for debugging) */}
              {exchangeRawData && (
                <details className="mb-4">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">View raw response data</summary>
                  <pre className="mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(exchangeRawData, null, 2)}
                  </pre>
                </details>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button
                type="button"
                onClick={closeExchangeModal}
                disabled={exchangeLoading}
                className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingHistory;
